/**
 * EntityRdfViewer
 *
 * Toolbar with Turtle / RDF/XML / JSON-LD buttons.
 * Serializes the already-loaded entity properties client-side —
 * no extra network request required.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { EntityProperty } from '@/types';
import type { PrefixMap } from '@utils/prefix.utils';
import { serializeToJsonLd, serializeToRdfXml, serializeToTurtle } from '@utils/rdfSerialize.utils';
import styles from './EntityPage.module.scss';

// ─── Component ────────────────────────────────────────────────────────────────

interface RdfFormat {
  label: string;
  serialize: (uri: string, props: EntityProperty[], pfx: PrefixMap) => string;
}

const RDF_FORMATS: RdfFormat[] = [
  { label: 'Turtle', serialize: serializeToTurtle },
  { label: 'RDF/XML', serialize: (u, p) => serializeToRdfXml(u, p) },
  { label: 'JSON-LD', serialize: serializeToJsonLd },
];

interface EntityRdfViewerProps {
  uri: string;
  properties: EntityProperty[];
  prefixes: PrefixMap;
}

const EntityRdfViewer = ({ uri, properties, prefixes }: EntityRdfViewerProps): JSX.Element => {
  const { t } = useTranslation();
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const activeFormat = RDF_FORMATS.find((f) => f.label === activeLabel) ?? null;

  const content = useMemo(
    () => activeFormat?.serialize(uri, properties, prefixes) ?? '',
    [activeFormat, uri, properties, prefixes]
  );

  function toggleFormat(fmt: RdfFormat) {
    setActiveLabel((prev) => (prev === fmt.label ? null : fmt.label));
    setCopied(false);
  }

  async function copyContent() {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  }

  function downloadContent() {
    if (!content || !activeFormat) return;
    try {
      const ext: Record<string, string> = { 'Turtle': 'ttl', 'RDF/XML': 'rdf', 'JSON-LD': 'jsonld' };
      const filename = `${uri.split(/[#/]/).filter(Boolean).pop() || 'entity'}.${ext[activeFormat.label] || 'txt'}`;
      
      // Add UTF-8 BOM for proper encoding in Excel and other applications
      const bom = '\uFEFF';
      const textWithBom = bom + content;
      const blob = new Blob([textWithBom], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[EntityRdfViewer] Download failed:', error);
    }
  }

  return (
    <div className={styles.rdfViewer}>
      <div className={styles.rdfToolbar}>
        {RDF_FORMATS.map((fmt) => (
          <button
            key={fmt.label}
            className={`${styles.rdfButton} ${activeLabel === fmt.label ? styles.rdfButtonActive : ''}`}
            onClick={() => toggleFormat(fmt)}
            aria-pressed={activeLabel === fmt.label}
          >
            {fmt.label}
          </button>
        ))}
      </div>

      {activeFormat && (
        <div className={styles.rdfCodeViewer}>
          <div className={styles.rdfCodeHeader}>
            <span className={styles.rdfCodeTitle}>{activeFormat.label}</span>
            <div className={styles.rdfCodeActions}>
              <button
                className={styles.rdfIconButton}
                onClick={downloadContent}
                title={t('entity.rdf.download')}
              >
                ⬇
              </button>
              <button
                className={styles.rdfIconButton}
                onClick={copyContent}
                title={copied ? t('entity.rdf.copied') : t('entity.rdf.copy')}
              >
                {copied ? '✓' : '⎘'}
              </button>
              <button
                className={styles.rdfIconButton}
                onClick={() => setActiveLabel(null)}
                title={t('entity.rdf.close')}
              >
                ✕
              </button>
            </div>
          </div>
          <pre className={styles.rdfCode}>
            <code>{content}</code>
          </pre>
        </div>
      )}
    </div>
  );
};

export default EntityRdfViewer;
